import {RequestState} from "data/enums";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {Bindings} from "data/constants/bindings";
import {ILeague} from "data/types/leagues";
import type {IGameplayStore} from "data/stores/gameplay/gameplay.store";

interface IInit {
	leagueId: number;
}

export interface ISettingsLeagueController extends ViewController<IInit> {
	i18n: ILocalizationStore;

	get league(): ILeague | undefined;
	get leagueContestName(): string | undefined;
	get isCommissioner(): boolean;
	get userId(): number | undefined;
}

@injectable()
export class SettingsLeagueController implements ISettingsLeagueController {
	private _league: ILeague | undefined;

	@observable _requestStateLeave: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore,
		@inject(Bindings.GameplayStore) private _gamePlayStore: IGameplayStore
	) {
		makeAutoObservable(this);
	}

	get league(): ILeague | undefined {
		return this._league;
	}

	get leagueContestName(): string | undefined {
		const contest = this._gamePlayStore.allContests.find(
			(it) => it.id === this.league?.startId
		);
		return contest?.name ? this.i18n.findValueByLocaleObject(contest?.name) : undefined;
	}

	get isCommissioner(): boolean {
		return Number(this.userId) === Number(this.league?.leagueManager?.userId);
	}

	get userId(): number | undefined {
		return this._userStore.user?.id;
	}

	async init(params: IInit) {
		this._league = this._leaguesStore.getLeagueById(params.leagueId);
		await this._gamePlayStore.requestContestsSafety();
	}

	dispose(): void {
		return;
	}
}
